<script lang="ts">
	import { Button, Checkbox, Radio, Slider } from '@Shelly/shelly-design-system';
	import { capFirstChar, mathNormalize } from 'src/helpers/utilities';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';

	export let device: Device;
	let waitForResponse = false;

	type TrvInterface = {
		lock: boolean;
		flip: boolean;
		t_units: string;
		brightness: number;
	};

	const trvInterface: TrvInterface = getTrvComponentByDevice(device)?.config?.sys?.ui || {
		lock: false,
		flip: false,
		t_units: 'C',
		brightness: 1,
	};

	trvInterface.brightness = mathNormalize(trvInterface.brightness, 1, 7);

	const saveInterface = async (): Promise<void> => {
		waitForResponse = true;
		await callBluTRV(device, 'Sys.SetConfig', {
			config: {
				ui: {
					lock: trvInterface.lock,
					flip: trvInterface.flip,
					t_units: trvInterface.t_units,
					brightness: trvInterface.brightness,
				},
			},
		});
		waitForResponse = false;
	};

	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: trvInterface.brightness },
			default: `Current brightness value is ${trvInterface.brightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: trvInterface.brightness },
			default: `Current brightness value is ${trvInterface.brightness}`,
		}),
	};
</script>

<Checkbox
	size="m"
	bind:checked={trvInterface.lock}
	class="flex-shrink-0 mr-s"
	info={$format('checkbox_blu_trv_interface_lock', {
		default: 'Disables the control of the device using its rotary knob',
	})}
	id="checkbox-6whj7qn"
>
	{$format('checkbox_blu_trv_lock', { default: 'TRV lock' })}
</Checkbox>
<Checkbox
	size="m"
	bind:checked={trvInterface.flip}
	class="flex-shrink-0 mr-s"
	info={$format('checkbox_blu_trv_interface_display', {
		default: "Rotates the TRV's display by 180°",
	})}
	id="checkbox-pjrpj6n"
>
	{$format('checkbox_blu_trv_display', { default: 'TRV display' })}
</Checkbox>
<Radio value="C" size="m" bind:selected={trvInterface.t_units} id="radio-4gagmeq"
	>{$format('checkbox_blu_trv_temp_celsius', {
		default: 'Temperature in °C',
	})}</Radio
>
<Radio value="F" size="m" class="mb-s" bind:selected={trvInterface.t_units} id="radio-9zhag48"
	>{$format('checkbox_blu_trv_temp_fahrenheit', {
		default: 'Temperature in °F',
	})}</Radio
>
<Slider
	id="slider-jh4f00m"
	{a11yProps}
	title={`${capFirstChar($format('label_brightness', { default: 'brightness' }))}:`}
	min={1}
	max={7}
	type="incremented"
	class="mb-s"
	unit=""
	bind:value={trvInterface.brightness}
/>

<Button
	loading={waitForResponse}
	disabled={waitForResponse}
	class="mt-s"
	size="m"
	on:click={saveInterface}
	width="full"
	>{$format('button_save_settings', {
		default: 'Save settings',
	})}</Button
>
